import { useRef, useState, useCallback, type SetStateAction } from 'react'

export interface UseControlledProps<Value> {
  /**
   * Controlled value.
   */
  controlled: Value
  /**
   * Default value.
   */
  default: Value
}

export type UseControlledReturned<Value> = [Exclude<Value, undefined>, (newValue: SetStateAction<Value>) => void]

export function useControlled<Value>({ controlled, default: defaultProp }: UseControlledProps<Value>): UseControlledReturned<Value> {
  const { current: isControlled } = useRef(controlled !== undefined)
  const [valueState, setValue] = useState(defaultProp)
  const value = (isControlled ? controlled : valueState) as Exclude<Value, undefined>

  const setValueIfUncontrolled = useCallback((newValue: SetStateAction<Value>) => {
    if (!isControlled) {
      setValue(newValue)
    }
  }, [isControlled])

  return [value, setValueIfUncontrolled]
}
